import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Brain, ArrowRight, RefreshCw, Sparkles, TrendingUp, AlertTriangle, Lightbulb, BarChart3, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const IMPACT_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#3b82f6' };
const IMPACT_LABELS = { high: 'Hoog', medium: 'Middel', low: 'Laag' };
const TYPE_ICONS = { pattern: BarChart3, opportunity: TrendingUp, risk: AlertTriangle, action: Lightbulb };

const CrossToolIntelligence = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchInsights = useCallback(async (force = false) => {
    try {
      if (force) setRefreshing(true);
      else setLoading(true);
      const url = force
        ? `${API}/cross-intelligence/emma-insights/refresh`
        : `${API}/cross-intelligence/emma-insights`;
      const method = force ? 'post' : 'get';
      const res = await axios({ method, url, headers });
      setInsights(res.data.insights || []);
      setError(null);
    } catch (e) {
      setError('Kon inzichten niet laden');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  const l = lang === 'nl' ? {
    title: 'Emma Inzichten',
    subtitle: 'Cross-tool patronen & kansen',
    refresh: 'Vernieuwen',
    refreshing: 'Analyseren...',
    noData: 'Gebruik meer AI tools om cross-tool inzichten te krijgen.',
    action: 'Actie',
    impact: 'Impact',
  } : {
    title: 'Emma Insights',
    subtitle: 'Cross-tool patterns & opportunities',
    refresh: 'Refresh',
    refreshing: 'Analyzing...',
    noData: 'Use more AI tools to get cross-tool insights.',
    action: 'Action',
    impact: 'Impact',
  };

  return (
    <div className="emma-insights-card" data-testid="emma-insights">
      <div className="emma-insights-header" onClick={() => setExpanded(!expanded)}>
        <div className="emma-insights-title-row">
          <div className="emma-insights-icon">
            <Sparkles size={18} />
          </div>
          <div>
            <h3 className="heading-3">{l.title}</h3>
            <span className="body-small text-secondary">{l.subtitle}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {insights.length > 0 && (
            <span className="emma-insights-count" data-testid="insights-count">{insights.length}</span>
          )}
          <button
            className="emma-insights-refresh"
            data-testid="insights-refresh-btn"
            onClick={(e) => { e.stopPropagation(); fetchInsights(true); }}
            disabled={refreshing}
          >
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            <span>{refreshing ? l.refreshing : l.refresh}</span>
          </button>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>

      {expanded && (
        <div className="emma-insights-body">
          {loading ? (
            <div className="emma-insights-loading">
              <Brain size={24} className="spin" />
              <span>{l.refreshing}</span>
            </div>
          ) : error ? (
            <div className="emma-insights-empty">{error}</div>
          ) : insights.length === 0 ? (
            <div className="emma-insights-empty">{l.noData}</div>
          ) : (
            <div className="emma-insights-list">
              {(Array.isArray(insights) ? insights : []).map((ins, i) => {
                const Icon = TYPE_ICONS[ins.type] || Lightbulb;
                const impactColor = IMPACT_COLORS[ins.impact] || '#3b82f6';
                return (
                  <div key={i} className="emma-insight-item" data-testid={`emma-insight-${i}`}>
                    <div className="emma-insight-top">
                      <div className="emma-insight-type-icon" style={{ color: impactColor }}>
                        <Icon size={16} />
                      </div>
                      <div className="emma-insight-content">
                        <div className="emma-insight-title">
                          <span>{ins.emoji || ''} {ins.title}</span>
                          <span className="emma-insight-impact" style={{ background: `${impactColor}20`, color: impactColor }}>
                            {IMPACT_LABELS[ins.impact] || ins.impact}
                          </span>
                        </div>
                        <p className="emma-insight-desc">{ins.description}</p>
                      </div>
                    </div>
                    {ins.action && (
                      <button
                        className="emma-insight-action"
                        data-testid={`emma-insight-action-${i}`}
                        onClick={() => ins.action_path && navigate(ins.action_path)}
                      >
                        <span>{ins.action}</span>
                        <ArrowRight size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CrossToolIntelligence;
